/**
 * Grading scales — display-only MIRROR of the Rust core (F3-B,
 * `core-rust/src/grading/scheme.rs`). The server is the AUTHORITY on the
 * grade (weighted gradebook + conversion); this registry only REPRODUCES the
 * presentation for the UI (gradebook, transcript, certificate).
 *
 * Numeric contract (identical to Rust):
 *   • `convert(raw)` — raw grade (0..1 fraction) → persisted native value
 *     (ve-0-20 → 0..20; the rest → 0..100 as a percentage carrier).
 *   • `displayValue(value)` — renders an already-persisted NATIVE value
 *     (what `cohort_enrollment.final_grade` / `certificate.final_grade` store).
 *   • `toDisplay(raw)` = `displayValue(convert(raw))`.
 *
 * Each scale self-describes with a `FeatureManifest` (`academy.grading.<key>`)
 * that feeds the feature catalog (`pnpm gen:features`).
 */

import type { FeatureManifest } from "../country/packs";

export type GradingSchemeKey = "ve-0-20" | "pct-100" | "letter" | "pass-fail";

export interface GradingSchemeStrategy {
  readonly key: GradingSchemeKey;
  /** Max value of the native scale (20 for ve-0-20; 100 for the rest). */
  readonly maxValue: number;
  /** Passing threshold as a raw FRACTION 0..1. */
  readonly passingThresholdPct: number;
  /** Short readable name (for selects/UI). */
  readonly label: string;
  /** Converts a raw grade (0..1) to the persistable native value. */
  convert(raw: number): number;
  /** Renders an already-persisted native value. */
  displayValue(value: number): string;
  /** Direct human string from the raw grade (0..1). */
  toDisplay(raw: number): string;
  /** Does it pass with this raw grade (0..1)? */
  isPassing(raw: number): boolean;
}

const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));
const roundTo = (x: number, n: number): number => {
  const f = 10 ** n;
  return Math.round(x * f) / f;
};

/** Base factory: shares convert/toDisplay/isPassing in terms of displayValue. */
function makeScheme(
  base: Pick<GradingSchemeStrategy, "key" | "maxValue" | "passingThresholdPct" | "label"> & {
    displayValue(value: number): string;
  },
): GradingSchemeStrategy {
  return {
    ...base,
    convert(raw: number) {
      return clamp01(raw) * base.maxValue;
    },
    toDisplay(raw: number) {
      return base.displayValue(clamp01(raw) * base.maxValue);
    },
    isPassing(raw: number) {
      return clamp01(raw) + Number.EPSILON >= base.passingThresholdPct;
    },
  };
}

/** Letter A–F from a 0..100 percentage (== `Letter::letter_for` in Rust). */
export function letterFor(pct: number): string {
  if (pct >= 90) {
    return "A";
  }
  if (pct >= 80) {
    return "B";
  }
  if (pct >= 70) {
    return "C";
  }
  if (pct >= 60) {
    return "D";
  }
  return "F";
}

const VE_0_20 = makeScheme({
  key: "ve-0-20",
  maxValue: 20,
  passingThresholdPct: 0.5,
  label: "0–20 (Venezuela)",
  displayValue(value) {
    const v = roundTo(value, 1);
    return Number.isInteger(v) ? String(Math.round(v)) : v.toFixed(1);
  },
});

const PCT_100 = makeScheme({
  key: "pct-100",
  maxValue: 100,
  passingThresholdPct: 0.6,
  label: "Porcentaje 0–100",
  displayValue(value) {
    return `${Math.round(value)}%`;
  },
});

const LETTER = makeScheme({
  key: "letter",
  maxValue: 100,
  passingThresholdPct: 0.6,
  label: "Letras A–F",
  displayValue(value) {
    return letterFor(value);
  },
});

const PASS_FAIL = makeScheme({
  key: "pass-fail",
  maxValue: 100,
  passingThresholdPct: 0.6,
  label: "Apto / No apto",
  displayValue(value) {
    return value + 1e-9 >= 60 ? "Apto" : "No apto";
  },
});

const SCHEMES: Record<GradingSchemeKey, GradingSchemeStrategy> = {
  "ve-0-20": VE_0_20,
  "pct-100": PCT_100,
  letter: LETTER,
  "pass-fail": PASS_FAIL,
};

/** Stable order of scales (for selects and catalog). */
export const GRADING_SCHEME_KEYS: readonly GradingSchemeKey[] = [
  "ve-0-20",
  "pct-100",
  "letter",
  "pass-fail",
];

export const DEFAULT_GRADING_SCHEME_KEY: GradingSchemeKey = "ve-0-20";

/** Returns the strategy for a key, or the `ve-0-20` default if unknown. */
export function gradingScheme(key: string | null | undefined): GradingSchemeStrategy {
  const k = (key ?? "").trim();
  return (SCHEMES as Record<string, GradingSchemeStrategy>)[k] ?? VE_0_20;
}

function pickDefined(o?: string | null): string | undefined {
  const t = (o ?? "").trim();
  return t.length > 0 ? t : undefined;
}

/** Resolves the effective scale by cohort → course → org → default precedence. */
export function resolveGradingScheme(
  cohort?: string | null,
  course?: string | null,
  org?: string | null,
): GradingSchemeStrategy {
  return gradingScheme(pickDefined(cohort) ?? pickDefined(course) ?? pickDefined(org));
}

// ─── DATA-DRIVEN grading models (group E) ─────────────────────────────────────
//
// Display-only mirror of `core-rust/src/grading/model.rs::ModelScheme`. The UI
// renders grades and does the live PREVIEW from the shape of a synced
// `grading_model` (max/decimals/bands/suffix/threshold) instead of the 4
// hardcoded scales. Its output is IDENTICAL to the system's 4 (parity
// verified in tests) so nothing already shown changes.

export type GradingModelKind = "points" | "percent" | "letter" | "pass_fail";

export interface GradingLetterBand {
  label: string;
  /** Min % 0..100 (inclusive) that grants this label. */
  minPct: number;
}

/** Minimal shape of a model to render/preview (camelCase). */
export interface GradingModelShape {
  /** Persisted key / identity (system_key or model id). */
  schemeKey?: string;
  kind: GradingModelKind;
  maxValue: number;
  /** Passing threshold as a FRACTION 0..1. */
  passingThreshold: number;
  decimals: number;
  letterBands?: GradingLetterBand[] | null;
  displaySuffix?: string | null;
}

/**
 * Raw `grading_model` row (snake_case) as it arrives via sync.
 *
 * All columns are nullable: in PowerSync they are by construction, and a
 * half-synced row must not blow up the scale resolution. An unusable row is
 * DISCARDED (`isUsableGradingModelRow`) and the resolution falls through to
 * the next precedence level — the gap is never filled with another scale's
 * numbers, which would mean privileging one again.
 */
export interface GradingModelRow {
  id: string;
  organization_id?: string | null;
  name?: string | null;
  kind?: string | null;
  max_value?: number | null;
  passing_threshold?: number | null;
  decimals?: number | null;
  letter_bands?: unknown;
  display_suffix?: string | null;
  /** SQLite has no booleans: 0/1 arrives via sync, `true`/`false` via REST. */
  is_system?: boolean | number | null;
  system_key?: string | null;
  /** A deleted model is IGNORED in the resolution (falls to the next level). */
  deleted_at?: string | null;
}

function parseBands(raw: unknown): GradingLetterBand[] {
  let arr: unknown = raw;
  if (typeof raw === "string") {
    try {
      arr = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) {
    return [];
  }
  return arr
    .map((b) => {
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- trust-boundary parse of jsonb bands
      const o = (typeof b === "object" && b !== null ? b : {}) as Record<string, unknown>;
      const rawMin = o.minPct ?? o.min_pct ?? 0;
      return {
        label: String(o.label ?? ""),
        minPct: typeof rawMin === "number" ? rawMin : Number(rawMin),
      };
    })
    .filter((b) => b.label.length > 0 && Number.isFinite(b.minPct));
}

const GRADING_MODEL_KINDS: readonly GradingModelKind[] = [
  "points",
  "percent",
  "letter",
  "pass_fail",
];

/** Coerces a string to a known `GradingModelKind` (default `points`). */
export function parseGradingModelKind(kind: string): GradingModelKind {
  return GRADING_MODEL_KINDS.find((k) => k === kind) ?? "points";
}

/** Normalizes a `grading_model` row (snake_case + jsonb) to `GradingModelShape`. */
export function modelShapeFromRow(row: GradingModelRow): GradingModelShape {
  const kind = parseGradingModelKind(row.kind ?? "");
  return {
    schemeKey: row.system_key ?? row.id,
    kind,
    // No "safe" defaults here: filling a gap with a specific scale's numbers
    // would mean privileging it again. An incomplete row is DISCARDED in the
    // resolution (`isUsableGradingModelRow`), not papered over.
    maxValue: row.max_value ?? 0,
    passingThreshold: row.passing_threshold ?? 0,
    decimals: row.decimals ?? 0,
    letterBands: kind === "letter" ? parseBands(row.letter_bands) : null,
    displaySuffix: row.display_suffix ?? null,
  };
}

/** Display-only strategy (doesn't require the key to be one of the system's 4). */
export type GradingDisplayStrategy = Omit<GradingSchemeStrategy, "key" | "label">;

function numericDisplay(value: number, decimals: number, suffix?: string | null): string {
  const dec = Math.max(0, Math.trunc(decimals));
  const v = roundTo(value, dec);
  const body = Number.isInteger(v) ? String(Math.round(v)) : v.toFixed(dec);
  return suffix ? `${body}${suffix}` : body;
}

/** Builds a display-only strategy from a model's shape. */
export function modelStrategy(shape: GradingModelShape): GradingDisplayStrategy {
  const displayValue = (value: number): string => {
    switch (shape.kind) {
      case "letter": {
        const bands = shape.letterBands ?? [];
        for (const b of bands) {
          if (value >= b.minPct) {
            return b.label;
          }
        }
        return bands.at(-1)?.label ?? "";
      }
      case "pass_fail": {
        return value + 1e-9 >= shape.passingThreshold * shape.maxValue ? "Apto" : "No apto";
      }
      default: {
        return numericDisplay(value, shape.decimals, shape.displaySuffix);
      }
    }
  };
  return {
    maxValue: shape.maxValue,
    passingThresholdPct: shape.passingThreshold,
    convert(raw) {
      return clamp01(raw) * shape.maxValue;
    },
    displayValue,
    toDisplay(raw) {
      return displayValue(clamp01(raw) * shape.maxValue);
    },
    isPassing(raw) {
      return clamp01(raw) + Number.EPSILON >= shape.passingThreshold;
    },
  };
}

/** The system's 4 shapes (mirror of `SystemModelSpec::ALL` in Rust). */
export function systemModelShapes(): Record<GradingSchemeKey, GradingModelShape> {
  const letterBands: GradingLetterBand[] = [
    { label: "A", minPct: 90 },
    { label: "B", minPct: 80 },
    { label: "C", minPct: 70 },
    { label: "D", minPct: 60 },
    { label: "F", minPct: 0 },
  ];
  return {
    "ve-0-20": {
      schemeKey: "ve-0-20",
      kind: "points",
      maxValue: 20,
      passingThreshold: 0.5,
      decimals: 1,
    },
    "pct-100": {
      schemeKey: "pct-100",
      kind: "percent",
      maxValue: 100,
      passingThreshold: 0.6,
      decimals: 0,
      displaySuffix: "%",
    },
    letter: {
      schemeKey: "letter",
      kind: "letter",
      maxValue: 100,
      passingThreshold: 0.6,
      decimals: 0,
      letterBands,
    },
    "pass-fail": {
      schemeKey: "pass-fail",
      kind: "pass_fail",
      maxValue: 100,
      passingThreshold: 0.6,
      decimals: 0,
    },
  };
}

// ─── Resolution of the ACTIVE scale (SINGLE decision point) ──────────────────
//
// Mirror of Rust's `resolve_cohort_model` (`grading/model.rs`). Before this
// the UI resolved ONLY by the legacy key (`resolveGradingScheme`), which
// knows the system's 4 scales and nothing else: an academy that DECLARED its
// own model (`grading_model` + `grading_model_id`) saw its report card
// rendered with the legacy scale, not its own. The engine honored it; the screen didn't.
//
// The decision lives here, and the default max of an evaluable item ALSO
// comes out of here: deriving it at a single point is what stops it from
// reappearing as a repeated constant on every screen.

/**
 * Does this row describe a USABLE scale?
 *
 * It's an INTEGRITY check, not a "correct" shape one: it doesn't require the
 * max to be 20, nor the pass mark to be at the halfway point, nor any other
 * school custom. Only that the numbers exist and are self-consistent, so a
 * half-synced row doesn't silently swallow the resolution.
 */
export function isUsableGradingModelRow(row: GradingModelRow): boolean {
  const max = row.max_value ?? Number.NaN;
  const threshold = row.passing_threshold ?? Number.NaN;
  if (!Number.isFinite(max) || max <= 0) {
    return false;
  }
  if (!Number.isFinite(threshold) || threshold < 0 || threshold > 1) {
    return false;
  }
  // A banded scale with no bands doesn't know how to render anything.
  return (
    parseGradingModelKind(row.kind ?? "") !== "letter" || parseBands(row.letter_bands).length > 0
  );
}

/** Resolution context: from most specific to most general (cohort→course→org). */
export interface GradingResolutionContext {
  /** Synced `grading_model` rows (the org's own; see `organizationId`). */
  models?: readonly GradingModelRow[] | null;
  /** Owning org: a model from ANOTHER org is ignored (isolation, like in Rust). */
  organizationId?: string | null;
  /** `grading_model_id` in cohort → course → org order. */
  modelIds?: readonly (string | null | undefined)[];
  /** Legacy `grading_scheme` key in cohort → course → org order. */
  legacyKeys?: readonly (string | null | undefined)[];
  /**
   * What to use when NOTHING is configured yet (freshly created org, rows
   * still syncing). It's a STARTUP value, not a privileged scale: the academy
   * decides its own in its settings and this stops being used.
   */
  fallback?: GradingDisplayStrategy;
}

/**
 * Resolves the EFFECTIVE scale. Precedence identical to the server:
 * `grading_model_id` cohort→course→org (isolated by org and skipping
 * deleted ones) → legacy key cohort→course→org → deployment default.
 *
 * `ve-0-20` is this deployment's DEFAULT, not a product law: an org that
 * declares a 0–10 or 0–100 scale gets it here without touching code.
 */
export function resolveActiveGradingModel(ctx: GradingResolutionContext): GradingDisplayStrategy {
  const models = ctx.models ?? [];
  const wantOrg = pickDefined(ctx.organizationId);
  for (const candidate of ctx.modelIds ?? []) {
    const id = pickDefined(candidate);
    if (!id) {
      continue;
    }
    const row = models.find(
      (m) =>
        m.id === id &&
        !m.deleted_at &&
        (wantOrg === undefined ||
          m.organization_id === null ||
          m.organization_id === undefined ||
          m.organization_id === wantOrg) &&
        isUsableGradingModelRow(m),
    );
    if (row) {
      return modelStrategy(modelShapeFromRow(row));
    }
  }
  const keys = ctx.legacyKeys ?? [];
  const key = keys.map(pickDefined).find((k) => k !== undefined);
  const declarada =
    key === undefined
      ? undefined
      : (SCHEMES as Record<string, GradingSchemeStrategy | undefined>)[key];
  return declarada ?? ctx.fallback ?? SCHEMES[DEFAULT_GRADING_SCHEME_KEY];
}

/**
 * Default max of an evaluable item, DERIVED from the active scale.
 *
 * It isn't "20 instead of 100": it's 20 with `ve-0-20`, 100 with `pct-100`
 * and 10 with a 0–10 scale declared by the academy. The teacher can still
 * set whatever max they want on a specific item (the gradebook normalizes to
 * a fraction); what disappears is the CONSTANT that contradicted the active scale.
 */
export function defaultItemMaxScore(scale: Pick<GradingDisplayStrategy, "maxValue">): number {
  return scale.maxValue;
}

/** Self-describing manifests of the scales (feature catalog). */
export function gradingSchemeManifests(): FeatureManifest[] {
  const descriptions: Record<GradingSchemeKey, string> = {
    "ve-0-20": "Escala venezolana 0–20 (aprobado con 10). Escala por defecto de la academia.",
    "pct-100": "Porcentaje 0–100 (aprobado con 60%).",
    letter: "Calificación por letras A–F (aprobado con D = 60%).",
    "pass-fail": "Apto / No apto (aprobado con 60%).",
  };
  return GRADING_SCHEME_KEYS.map((key) => ({
    key: `academy.grading.${key}`,
    name: SCHEMES[key].label,
    description: descriptions[key],
    scope: "org" as const,
    status: "stable" as const,
  }));
}
